import { FC, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { IEmployee } from 'routes/client/structure/employees/types';
import { useActiveClientContext } from 'store/active-client';
import { buildClientEntity, buildClientUrl } from 'utils/helpers/client';
import { useTranslation } from 'react-i18next';
import TooltipWrapper from 'components/TooltipWrapper';
import FetchTable from 'components/table/FetchTable';
import { Column } from 'react-table';
import { buildFilters } from 'components/table/utils/filtersConfig';

interface IProps {
  groupId: string;
}

const Moderators: FC<IProps> = ({ groupId }) => {
  const { t } = useTranslation();
  const { client } = useActiveClientContext();
  const history = useHistory();

  const statusOptions = [
    { value: 'active', label: t('table.activeStatus') },
    { value: 'inactive', label: t('table.inactiveStatus') },
    { value: 'released', label: t('table.releasedStatus') },
  ];

  const columns: Array<Column<IEmployee>> = [
    {
      Header: 'ID',
      accessor: 'id',
    },
    {
      Header: t('structure.groups.name') || '',
      accessor: 'name',
      Cell: ({ value, row }) => {
        const { id, can_edit: canEdit } = row.original;
        if (canEdit)
          return (
            <span
              className="ml-1 u-underline u-color-inherit u-cursor-pointer"
              onClick={() => history.push(buildClientUrl(`/structure/employees/edit/${id}`, client))}
            >
              {value}
            </span>
          );

        return value;
      },
    },
    {
      Header: t('structure.employees.position') || '',
      accessor: 'position',
      disableSortBy: true,
    },
    {
      Header: t('structure.roles.title') || '',
      accessor: 'roles',
      Cell: ({ value }) => {
        const fullRoles = value.join(', ');
        const rolesPreview = fullRoles.length > 20 ? `${fullRoles.substr(0, 20)}...` : fullRoles;

        return (
          <TooltipWrapper
            id="tooltip-roles"
            overlay={fullRoles}
          >
            <span>{rolesPreview}</span>
          </TooltipWrapper>
        );
      },
      disableSortBy: true,
    },
  ];

  if (client?.permissions_scope) {
    columns.push({
      Header: t('structure.employees.limitedAccess') || '',
      accessor: 'has_limited_access',
      Cell: ({ value, row }) => {
        const { id } = row.original;
        return (
          <span
            className="u-underline u-cursor-pointer"
            style={{ color: value ? '#ef476f' : '#28a745' }}
            onClick={() =>
              history.push(`${buildClientUrl(`/structure/employees/edit/${id}`, client)}?isOrganizeGroup=true`)
            }
          >
            {value ? t('common.has') : t('common.hasNot')}
          </span>
        );
      },
    });
  }

  const tableFilters = useMemo(() => {
    return [
      buildFilters({
        name: 'status',
        placeholder: t('structure.employees.status'),
        options: statusOptions,
        isMulti: true,
        showTotal: true,
        allowSelectAll: true,
      }),
    ];
  }, [t]);

  return (
    <div className="position-relative mt-5">
      <h3
        className="h5 position-absolute"
        style={{ top: '1rem', left: 0 }}
      >
        {t('structure.groups.moderators')}
      </h3>
      <FetchTable<IEmployee>
        entityName={buildClientEntity('group-moderators', client)}
        entityEndpoint={`users/group-rights/${groupId}`}
        columns={columns}
        pageLimit={10}
        filters={tableFilters}
        hideSearch
        pagination
        useBackendSorting
      />
    </div>
  );
};

export default Moderators;
